/**
 * Kanban experiment backlog — Ideas / Planned / Running / Decided.
 * Ideas from local capture + learning loop; Planned = drafts;
 * Running = active; Decided = completed/paused with a recorded decision.
 */

import { useEffect, useMemo, useState } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Add01Icon,
  ArrowRight01Icon,
  BulbIcon,
  ChampionIcon,
  Delete01Icon,
} from "@hugeicons/core-free-icons"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { formatNumeric } from "@/lib/utils/stringUtils"
import { CaptureIdeaDialog } from "@/components/experiments/CaptureIdeaDialog"
import {
  addExperimentIdea,
  iceScore,
  listExperimentIdeas,
  removeExperimentIdea,
  type ExperimentIdea,
} from "@/lib/utils/experimentIdeas"
import {
  getExperimentLog,
  nextBestActionFromLog,
  type ExperimentLogEntry,
} from "@/lib/utils/experimentLog"
import type { Experiment } from "@/hooks/useExperiments"
import {
  calculateImprovement,
  significanceForGoal,
  determineWinner,
} from "@/hooks/useExperiments"
import { CreateOrUpgradeButton } from "@/components/shared/plan-usage"

type DraftLike = {
  draftId: string
  experimentName: string
  testGoal?: string
  testType?: string
  currentStep?: number
  updatedAt: string
  componentPageUrl?: string
}

type ExperimentBacklogProps = {
  propertyId: string
  experiments: Experiment[]
  drafts: DraftLike[]
  onOpenExperiment: (experiment: Experiment) => void
  onContinueDraft: (draft: DraftLike) => void
  onDeleteDraft: (draftId: string) => void
  onCreateFromIdea: (idea: ExperimentIdea) => void
  onNewExperiment: () => void
  /** True when the plan's active test limit is reached — "New" opens the paywall instead. */
  atTestLimit?: boolean
  testLimit?: number
  planName?: string
}

type LaneId = "ideas" | "planned" | "running" | "decided"

export function ExperimentBacklog({
  propertyId,
  experiments,
  drafts,
  onOpenExperiment,
  onContinueDraft,
  onDeleteDraft,
  onCreateFromIdea,
  onNewExperiment,
  atTestLimit = false,
  testLimit,
  planName,
}: ExperimentBacklogProps) {
  const [ideas, setIdeas] = useState<ExperimentIdea[]>(() =>
    listExperimentIdeas(propertyId),
  )
  const [log, setLog] = useState<ExperimentLogEntry[]>(() =>
    getExperimentLog(propertyId),
  )
  const [captureOpen, setCaptureOpen] = useState(false)

  useEffect(() => {
    setIdeas(listExperimentIdeas(propertyId))
    setLog(getExperimentLog(propertyId))
  }, [propertyId])

  const running = useMemo(
    () => experiments.filter((e) => e.status === "active"),
    [experiments],
  )
  const decided = useMemo(() => {
    const decidedIds = new Set(log.map((l) => l.experimentId))
    return experiments.filter(
      (e) =>
        e.status === "completed" ||
        e.status === "paused" ||
        e.status === "archived" ||
        decidedIds.has(e.experimentId),
    )
  }, [experiments, log])

  const latestWin = log.find((l) => l.action === "ship" || (l.action === "stop" && l.winnerVariantId))
  const nextAction = latestWin ? nextBestActionFromLog(latestWin) : null

  const lanes: {
    id: LaneId
    label: string
    meta: string
    count: number
  }[] = [
    { id: "ideas", label: "Ideas", meta: "Backlog", count: ideas.length },
    { id: "planned", label: "Planned", meta: "Drafts", count: drafts.length },
    { id: "running", label: "Running", meta: "Live", count: running.length },
    { id: "decided", label: "Decided", meta: "Learning", count: decided.length },
  ]

  return (
    <div className="space-y-2">
      {nextAction && latestWin && (
        <Card className="border-border/30 gap-0">
          <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="size-8 rounded-lg bg-muted/40 border border-border/40 flex items-center justify-center shrink-0">
                <HugeiconsIcon icon={ChampionIcon} size={16} className="text-muted-foreground" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="size-1.5 rounded-[3px] bg-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">
                    Next best action
                  </span>
                </div>
                <p className="text-sm font-medium text-foreground">{nextAction.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {nextAction.description}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => {
                const idea = addExperimentIdea({
                  propertyId,
                  title: `Follow-up: ${latestWin.experimentName}`,
                  hypothesis: `Repeat the winning pattern from ${latestWin.experimentName}`,
                  pageUrl: latestWin.pageUrl,
                  source: "learning",
                  impact: 8,
                  confidence: 7,
                  ease: 6,
                })
                setIdeas(listExperimentIdeas(propertyId))
                onCreateFromIdea(idea)
              }}
            >
              {nextAction.ctaLabel}
              <HugeiconsIcon icon={ArrowRight01Icon} size={14} className="ml-1.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 xl:h-[calc(100dvh-22rem)] xl:min-h-[420px]">
        {lanes.map((lane) => (
          <div
            key={lane.id}
            className="surface-raised flex flex-col min-h-[240px] xl:min-h-0 xl:h-full overflow-hidden"
          >
            <div className="h-11 px-3 border-b border-border bg-muted/50 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <span
                  aria-hidden
                  className={cn(
                    "size-1.5 rounded-[3px] shrink-0",
                    lane.id === "running" && "bg-live",
                    lane.id === "planned" && "bg-foreground/30",
                    lane.id === "ideas" && "ring-1 ring-inset ring-foreground/30",
                    lane.id === "decided" && "bg-foreground",
                  )}
                />
                <span className="text-[13px] font-medium text-foreground">
                  {lane.label}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {lane.count}
                </span>
              </div>
              {lane.id === "ideas" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs font-medium text-muted-foreground"
                  onClick={() => setCaptureOpen(true)}
                >
                  <HugeiconsIcon icon={Add01Icon} size={12} className="mr-1" />
                  Capture
                </Button>
              )}
              {lane.id === "planned" && (
                <CreateOrUpgradeButton
                  atLimit={atTestLimit}
                  onClick={onNewExperiment}
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs font-medium text-muted-foreground"
                  noun="experiments"
                  limit={testLimit}
                  planName={planName}
                >
                  <HugeiconsIcon icon={Add01Icon} size={12} className="mr-1" />
                  New
                </CreateOrUpgradeButton>
              )}
            </div>

            <div className="p-2 space-y-2 flex-1 min-h-0 overflow-y-auto">
              {lane.id === "ideas" &&
                (ideas.length === 0 ? (
                  <EmptyLane
                    icon={BulbIcon}
                    text="Capture an idea or promote a learning."
                  />
                ) : (
                  ideas.map((idea) => (
                    <IdeaCard
                      key={idea.id}
                      idea={idea}
                      onPromote={() => onCreateFromIdea(idea)}
                      onDelete={() => {
                        removeExperimentIdea(idea.id)
                        setIdeas(listExperimentIdeas(propertyId))
                      }}
                    />
                  ))
                ))}

              {lane.id === "planned" &&
                (drafts.length === 0 ? (
                  <EmptyLane text="Drafts appear here while you build." />
                ) : (
                  drafts.map((draft) => (
                    <DraftCard
                      key={draft.draftId}
                      draft={draft}
                      onContinue={() => onContinueDraft(draft)}
                      onDelete={() => onDeleteDraft(draft.draftId)}
                    />
                  ))
                ))}

              {lane.id === "running" &&
                (running.length === 0 ? (
                  <EmptyLane text="No live tests. Launch one from Planned." />
                ) : (
                  running.map((exp) => (
                    <ExperimentCard
                      key={exp.experimentId}
                      experiment={exp}
                      onOpen={() => onOpenExperiment(exp)}
                    />
                  ))
                ))}

              {lane.id === "decided" &&
                (decided.length === 0 ? (
                  <EmptyLane text="Called tests and learnings land here." />
                ) : (
                  decided.map((exp) => (
                    <ExperimentCard
                      key={exp.experimentId}
                      experiment={exp}
                      decision={log.find((l) => l.experimentId === exp.experimentId)}
                      onOpen={() => onOpenExperiment(exp)}
                    />
                  ))
                ))}
            </div>
          </div>
        ))}
      </div>

      <CaptureIdeaDialog
        open={captureOpen}
        onOpenChange={setCaptureOpen}
        propertyId={propertyId}
        onCaptured={() => setIdeas(listExperimentIdeas(propertyId))}
      />
    </div>
  )
}

function EmptyLane({
  text,
  icon,
}: {
  text: string
  icon?: typeof BulbIcon
}) {
  return (
    <div className="h-full min-h-[160px] rounded-lg border border-dashed border-border flex flex-col items-center justify-center px-3 py-8 text-center">
      <HugeiconsIcon
        icon={icon ?? BulbIcon}
        size={16}
        className="mb-2 text-muted-foreground/70"
      />
      <p className="text-xs text-muted-foreground leading-snug max-w-[24ch]">{text}</p>
    </div>
  )
}

const ITEM =
  "rounded-lg bg-card p-3 ring-1 ring-foreground/[0.08] space-y-2 transition-colors"

const CHROME_BUTTON =
  "inline-flex h-7 flex-1 items-center justify-center whitespace-nowrap rounded-lg bg-card px-2.5 text-xs font-medium text-foreground ring-1 ring-foreground/10 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

function IdeaCard({
  idea,
  onPromote,
  onDelete,
}: {
  idea: ExperimentIdea
  onPromote: () => void
  onDelete: () => void
}) {
  return (
    <div className={ITEM}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-medium text-foreground leading-snug line-clamp-2">
          {idea.title}
        </p>
        <span className="inline-flex h-5 shrink-0 items-center rounded-md bg-muted px-1.5 text-xs font-medium tabular-nums text-foreground">
          ICE {iceScore(idea)}
        </span>
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2">{idea.hypothesis}</p>
      {idea.pageUrl && (
        <p className="text-xs text-muted-foreground truncate">{idea.pageUrl}</p>
      )}
      <div className="flex items-center gap-1.5 pt-0.5">
        <button type="button" className={CHROME_BUTTON} onClick={onPromote}>
          Plan it
        </button>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Delete idea"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <HugeiconsIcon icon={Delete01Icon} size={14} />
        </Button>
      </div>
    </div>
  )
}

function DraftCard({
  draft,
  onContinue,
  onDelete,
}: {
  draft: DraftLike
  onContinue: () => void
  onDelete: () => void
}) {
  const step = Math.min((draft.currentStep ?? 0) + 1, 3)
  return (
    <div
      className={cn(ITEM, "cursor-pointer hover:bg-muted/40")}
      onClick={onContinue}
    >
      <p className="text-[13px] font-medium text-foreground truncate">
        {draft.experimentName || "Untitled draft"}
      </p>
      <p className="text-xs text-muted-foreground">
        Setup step {step} of 3
        {draft.testType ? ` · ${draft.testType === "element" ? "No-code" : "Page"}` : ""}
      </p>
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button type="button" className={CHROME_BUTTON} onClick={onContinue}>
          Open editor
        </button>
        <Button
          variant="ghost"
          size="sm"
          aria-label="Delete draft"
          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <HugeiconsIcon icon={Delete01Icon} size={14} />
        </Button>
      </div>
    </div>
  )
}

function ExperimentCard({
  experiment,
  decision,
  onOpen,
}: {
  experiment: Experiment
  decision?: ExperimentLogEntry
  onOpen: () => void
}) {
  const variants = Array.isArray(experiment.variants) ? experiment.variants : []
  const control = variants.find((v) => v.isControl)
  const { winner, significance } = determineWinner(variants, 95, experiment.testGoal)
  const calcSignificance = significanceForGoal(experiment.testGoal)
  const lift =
    winner && control
      ? calculateImprovement(control.conversionRate || 0, winner.conversionRate || 0)
      : null
  const visitors = variants.reduce((s, v) => s + (v.sessions || 0), 0)
  const confidence =
    significance ||
    (control
      ? variants
          .filter((v) => !v.isControl)
          .reduce(
            (max, v) =>
              Math.max(
                max,
                calcSignificance(
                  control.conversions || 0,
                  control.sessions || 0,
                  v.conversions || 0,
                  v.sessions || 0,
                ),
              ),
            0,
          )
      : 0)
  const letter = winner ? String.fromCharCode(65 + variants.findIndex((v) => v.id === winner.id)) : "B"
  // The answer: a winner nobody has acted on yet.
  const isAnswer = lift != null && lift > 0 && !decision && experiment.status !== "archived"
  const signed = lift == null ? "" : `${lift < 0 ? "\u2212" : "+"}${Math.round(Math.abs(lift))}%`

  return (
    <div
      className={cn(
        ITEM,
        "cursor-pointer",
        isAnswer ? "bg-primary-tint/50 hover:bg-primary-tint/70" : "hover:bg-muted/40",
      )}
      onClick={onOpen}
    >
      <p className="text-[13px] font-medium text-foreground leading-snug line-clamp-2">
        {experiment.experimentName}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {lift != null && lift > 0 ? (
          <span
            className={cn(
              "inline-flex h-[22px] items-center whitespace-nowrap rounded-md px-[7px] text-xs font-medium",
              isAnswer
                ? "bg-primary-tint text-primary-strong ring-1 ring-primary-tint-ring"
                : "bg-muted text-foreground",
            )}
          >
            {letter} won {signed}
          </span>
        ) : experiment.status === "active" ? (
          <span className="text-xs text-muted-foreground">Too early to call</span>
        ) : (
          <span className="inline-flex h-[22px] items-center whitespace-nowrap rounded-md bg-muted px-[7px] text-xs font-medium text-foreground/70">
            No clear difference
          </span>
        )}
        {decision?.action === "ship" && (
          <span className="text-xs text-muted-foreground">Applied</span>
        )}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground tabular-nums">
        <span>{formatNumeric(visitors)} visitors</span>
        <span aria-hidden>·</span>
        <span>{isNaN(confidence) ? "–" : `${Math.round(confidence)}% confidence`}</span>
      </div>
    </div>
  )
}
